import { useEffect } from "react";
import type { Role } from "./domain";
import { autoFitColumnWidth } from "./registryColumnAutoFit";

type StoredTableWidths = { version: 1; widths: number[] };
const storagePrefix = "pharmhub:all-table-widths:v1";
const minimumWidth = 72;
const maximumWidth = 960;

function clamp(value: number): number {
  return Math.round(Math.min(maximumWidth, Math.max(minimumWidth, value)));
}

function tableIdentity(table: HTMLTableElement, routeKey: string): string {
  const labels = Array.from(table.tHead?.rows[0]?.cells ?? [])
    .map((cell) => (cell.textContent || "").replace(/\s+/g, " ").trim())
    .join("|");
  return encodeURIComponent(`${routeKey}:${labels}`.slice(0, 360));
}

function readWidths(key: string): number[] {
  try {
    const stored = JSON.parse(window.localStorage.getItem(key) || "null") as StoredTableWidths | null;
    return stored?.version === 1 && Array.isArray(stored.widths)
      ? stored.widths.map((value) => Number.isFinite(Number(value)) ? clamp(Number(value)) : 0)
      : [];
  } catch {
    return [];
  }
}

function persistWidths(key: string, headers: HTMLTableCellElement[]): void {
  try {
    window.localStorage.setItem(key, JSON.stringify({
      version: 1,
      widths: headers.map((header) => clamp(header.getBoundingClientRect().width)),
    } satisfies StoredTableWidths));
  } catch {
    // A private session or exhausted quota must not block a registry.
  }
}

export function GlobalResizableTables({ role, userId, routeKey }: { role: Role; userId: string; routeKey: string }) {
  useEffect(() => {
    const root = document.getElementById("main-workspace");
    if (!root) return;
    const workspace = root;
    let frame = 0;

    function enhance(table: HTMLTableElement): void {
      if (table.classList.contains("resizable-registry-table")) return;
      const headers = Array.from(table.tHead?.rows[0]?.cells ?? []);
      if (headers.length < 2 || headers.some((header) => header.colSpan > 1)) return;
      const identity = tableIdentity(table, routeKey);
      const alreadyEnhanced = table.dataset.globalResizeKey === identity && headers.every((header) => header.querySelector(":scope > .global-table-column-resizer"));
      table.dataset.globalResizeKey = identity;
      table.classList.add("global-resizable-table");
      const key = `${storagePrefix}:${encodeURIComponent(userId || "anonymous")}:${role}:${identity}`;
      const stored = readWidths(key);
      const syncTableWidth = () => {
        const total = headers.reduce((sum, item) => sum + clamp(Number.parseFloat(item.style.width) || item.getBoundingClientRect().width), 0);
        table.style.minWidth = `${total}px`;
      };
      if (alreadyEnhanced) {
        headers.forEach((header, index) => {
          if (!header.dataset.manualResize) header.style.width = `${autoFitColumnWidth(table, index)}px`;
        });
        syncTableWidth();
        return;
      }

      headers.forEach((header, index) => {
        header.classList.add("global-resizable-table__header");
        header.style.width = `${stored[index] || autoFitColumnWidth(table, index)}px`;
        if (stored[index]) header.dataset.manualResize = "true";
        header.querySelectorAll(":scope > .global-table-column-resizer").forEach((handle) => handle.remove());
        const handle = document.createElement("span");
        handle.className = "global-table-column-resizer";
        handle.setAttribute("role", "separator");
        handle.setAttribute("aria-orientation", "vertical");
        handle.setAttribute("aria-label", `Изменить ширину колонки «${(header.textContent || `№ ${index + 1}`).trim()}»`);
        handle.tabIndex = 0;

        const setWidth = (width: number) => {
          const next = clamp(width);
          header.dataset.manualResize = "true";
          header.style.width = `${next}px`;
          handle.setAttribute("aria-valuenow", String(next));
          syncTableWidth();
        };
        handle.setAttribute("aria-valuemin", String(minimumWidth));
        handle.setAttribute("aria-valuemax", String(maximumWidth));
        handle.setAttribute("aria-valuenow", String(clamp(header.getBoundingClientRect().width || minimumWidth)));
        handle.addEventListener("pointerdown", (event) => {
          if (event.button !== 0) return;
          event.preventDefault();event.stopPropagation();
          const startX = event.clientX;
          const startWidth = header.getBoundingClientRect().width;
          const move = (next: PointerEvent) => setWidth(startWidth + next.clientX - startX);
          const finish = () => {
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", finish);
            window.removeEventListener("pointercancel", finish);
            document.body.classList.remove("registry-column-resizing");
            persistWidths(key, headers);
          };
          document.body.classList.add("registry-column-resizing");
          window.addEventListener("pointermove", move);
          window.addEventListener("pointerup", finish, { once:true });
          window.addEventListener("pointercancel", finish, { once:true });
        });
        handle.addEventListener("keydown", (event) => {
          if (!["ArrowLeft", "ArrowRight", "Home"].includes(event.key)) return;
          event.preventDefault();event.stopPropagation();
          const current = header.getBoundingClientRect().width;
          const fallback = autoFitColumnWidth(table, index);
          setWidth(event.key === "Home" ? fallback : current + (event.key === "ArrowRight" ? (event.shiftKey ? 25 : 10) : -(event.shiftKey ? 25 : 10)));
          persistWidths(key, headers);
        });
        handle.addEventListener("dblclick", (event) => {
          event.preventDefault();event.stopPropagation();
          setWidth(autoFitColumnWidth(table, index));
          persistWidths(key, headers);
        });
        handle.addEventListener("click", (event) => { event.preventDefault();event.stopPropagation(); });
        header.append(handle);
      });
      syncTableWidth();
    }

    function scan(): void {
      workspace.querySelectorAll<HTMLTableElement>("table").forEach(enhance);
    }
    function scheduleScan(): void {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(scan);
    }
    scan();
    const observer = new MutationObserver(scheduleScan);
    observer.observe(workspace, { childList:true, subtree:true, characterData:true });
    return () => { observer.disconnect();window.cancelAnimationFrame(frame); };
  }, [role, routeKey, userId]);
  return null;
}
